import { isEqual } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import uuid from 'react-uuid';
import { ButtonState } from 'src/components/design-system/Button';
import useAsync from 'src/hooks/useAsync';
import {
  ReceiptLineItemError,
  ReceiptLineItemInfo,
  ReceiptLineItemWarning,
} from 'src/sections/receipt_details/errors/useReceiptLineItemErrors';
import axiosInstance from 'src/utils/axios';
import { GlCodesObject } from './useGlCodesApi';
import { ReceiptItemNoteItem, ReceiptItemNotesObject } from './useReceiptItemNotesApi';

export interface ReceiptLineItem {
  rowId: string;
  canDeleteReceiptLineItem: ButtonState;
  complete: string;
  poQty: number;
  currentDocQty: number;
  previousDocQty: number;
  varianceQty: number;
  item: string;
  itemDescription: string;
  poglCode: string;
  poglCodeDescription: string;
  uom: string;
  itemNoteId: number;
  itemNoteValue: string;
  isNewLineItem?: boolean;
  receivingReceiptLineItemId: number;
  poLineItemNumber: number;
  isCombinedLineItem?: boolean;
  errors?: ReceiptLineItemError;
  warnings?: ReceiptLineItemWarning;
  infos?: ReceiptLineItemInfo;
  isNewlyAddedLineItemNow: boolean;
}

export interface ReceiptLineItemWithSelected extends ReceiptLineItem {
  selected: boolean;
}

const useReceiptLineItemsApi = (
  receiptId: string,
  glCodesObject: GlCodesObject,
  receiptItemNotesObject: ReceiptItemNotesObject,
  fullyCompletedItemNotes: ReceiptItemNoteItem
) => {
  const [initialReceiptLineItems, setInitialReceiptLineItems] = useState<ReceiptLineItem[]>([]);

  const call = useCallback(async () => {
    try {
      if (
        glCodesObject &&
        receiptItemNotesObject &&
        Object.keys(receiptItemNotesObject).length &&
        Object.keys(glCodesObject).length
      ) {
        const res = await axiosInstance.get<ReceiptLineItem[]>(`Receipt/${receiptId}/LineItems`);
        const receiptLineItems: ReceiptLineItem[] = [...res?.data].map((item) => ({
          ...item,
          selected: false,
          rowId: uuid(),
          poglCodeDescription: glCodesObject[item.poglCode]?.label,
          itemNoteId: item.itemNoteId || fullyCompletedItemNotes.itemNoteId,
          itemNoteValue: item.itemNoteId
            ? receiptItemNotesObject[item.itemNoteId].itemNoteValue
            : fullyCompletedItemNotes.itemNoteValue,
        }));
        setInitialReceiptLineItems(receiptLineItems);
        return receiptLineItems;
      } else {
        return [];
      }
    } catch (error) {
      console.error(error);
      throw new Error(error);
    }
  }, [
    fullyCompletedItemNotes?.itemNoteId,
    fullyCompletedItemNotes?.itemNoteValue,
    glCodesObject,
    receiptId,
    receiptItemNotesObject,
  ]);

  const { execute, isLoading, value, setValue } = useAsync<ReceiptLineItem[]>(
    call,
    true,
    undefined,
    []
  );

  const isReceiptLineItemsDirty = useMemo(
    () => !isEqual(initialReceiptLineItems, value),
    [initialReceiptLineItems, value]
  );

  return {
    isReceiptLineItemsLoading: isLoading,
    receiptLineItems: value,
    setReceiptLineItems: setValue,
    getReceiptLineItems: execute,
    isReceiptLineItemsDirty,
  };
};

export default useReceiptLineItemsApi;
